import { useEffect, useMemo, useState } from "react";
import {
  errorMessage,
  listConnectionResources,
  setAllowedResources,
} from "../api";
import type { CatalogProvider, Connection, ProviderResource } from "../types";
import type { ToastState } from "./Toast";

interface Props {
  provider: CatalogProvider;
  conn: Connection;
  /** Set for a team connection: the selection here is shared, so say with whom. */
  teamName: string | null;
  notify: (message: string, kind?: ToastState["kind"]) => void;
  onClose: () => void;
}

/**
 * Which of the account's resources (GitHub: repositories) this connection may touch.
 *
 * A dialog for the same reason the tool list is one -- it is a decision about one
 * account, and the list can be long. Unlike the tool dialog it has a Save: the list is
 * fetched from the provider when it opens (it is not in Firestore), and an empty
 * selection is not a storable state, so writing through on every tick would either spam
 * a callable per click or trap the user the moment they unticked the last box.
 *
 * "All" is stored as null, not as the full list frozen at save time: null means
 * everything the credential can reach, repositories created next week included.
 */
export default function ResourcesDialog({
  provider,
  conn,
  teamName,
  notify,
  onClose,
}: Props) {
  const noun = provider.resources?.noun ?? "resources";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hint, setHint] = useState(provider.resources?.hint ?? "");
  const [resources, setResources] = useState<ProviderResource[]>([]);
  /** The draft: everything, or the ticked ids. Saved only on Save. */
  const [all, setAll] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    listConnectionResources({ connectionId: conn.id })
      .then((res) => {
        if (cancelled) return;
        const { resources: listed, selected } = res.data;

        // A selected id the listing did not return (the listing is best-effort and
        // truncated; a repo can also be renamed or lose access) still enforces, so it
        // must stay visible and untickable-off -- silently dropping it on the next Save
        // would widen nothing but *lose* a restriction the user chose.
        const listedIds = new Set(listed.map((r) => r.id));
        const kept = (selected ?? [])
          .filter((id) => !listedIds.has(id))
          .map((id) => ({
            id,
            label: id,
            detail: `not in the list ${provider.name} returned — kept until you untick it`,
          }));

        setHint(res.data.hint);
        setResources([...kept, ...listed]);
        setAll(selected === null);
        setPicked(new Set(selected ?? []));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(errorMessage(err));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conn.id, provider.name]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return resources;
    return resources.filter(
      (r) =>
        r.label.toLowerCase().includes(needle) ||
        (r.detail ?? "").toLowerCase().includes(needle)
    );
  }, [resources, filter]);

  function toggle(id: string, on: boolean) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const savable = !loading && !loadError && (all || picked.size > 0);

  async function save() {
    setSaving(true);
    try {
      // Stored in display order so the model-facing refusal message lists them the way
      // the user last saw them.
      const selection = all
        ? null
        : resources.map((r) => r.id).filter((id) => picked.has(id));
      await setAllowedResources({ connectionId: conn.id, resources: selection });
      notify(
        selection
          ? `${conn.accountLabel}: limited to ${selection.length} ` +
              `${selection.length === 1 ? noun.replace(/ies$/, "y").replace(/s$/, "") : noun}.`
          : `${conn.accountLabel}: can reach all its ${noun} again.`
      );
      onClose();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${noun} for ${conn.accountLabel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div>
            <h2>
              {conn.accountLabel} · {provider.name}
            </h2>
            <p className="modal-sub">
              Which {noun} this connection's tools may touch
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal-body">
          {hint && <p className="tools-hint">{hint}</p>}

          {/* Shared account, shared narrowing -- same warning as the tool dialog. */}
          {teamName && (
            <p className="tools-hint">
              This account is shared: the selection here applies to everyone in{" "}
              {teamName}.
            </p>
          )}

          {loading ? (
            <p className="tools-hint">Asking {provider.name} what this account can reach…</p>
          ) : loadError ? (
            <p className="manual-error">{loadError}</p>
          ) : (
            <>
              <div className="tools" role="radiogroup" aria-label={`Which ${noun}`}>
                <label className="tool">
                  <input
                    type="radio"
                    name="resource-scope"
                    checked={all}
                    disabled={saving}
                    onChange={() => setAll(true)}
                  />
                  <span className="tool-body">
                    <span className="tool-name">All {noun}</span>
                    <span className="tool-desc">
                      Everything this account can reach — including {noun} it gains later.
                    </span>
                  </span>
                </label>
                <label className="tool">
                  <input
                    type="radio"
                    name="resource-scope"
                    checked={!all}
                    disabled={saving}
                    onChange={() => setAll(false)}
                  />
                  <span className="tool-body">
                    <span className="tool-name">Only the {noun} selected below</span>
                    <span className="tool-desc">
                      Calls that reach for anything else are refused, whatever the tool.
                    </span>
                  </span>
                </label>
              </div>

              {!all && (
                <>
                  {resources.length > 8 && (
                    <input
                      className="search-input"
                      placeholder={`Filter ${noun}…`}
                      value={filter}
                      spellCheck={false}
                      onChange={(e) => setFilter(e.target.value)}
                      aria-label={`Filter ${noun}`}
                    />
                  )}

                  <div className="tools">
                    {visible.map((r) => (
                      <label key={r.id} className="tool">
                        <input
                          type="checkbox"
                          checked={picked.has(r.id)}
                          disabled={saving}
                          onChange={(e) => toggle(r.id, e.target.checked)}
                        />
                        <span className="tool-body">
                          <span className="tool-name">
                            <code>{r.label}</code>
                          </span>
                          {r.detail && <span className="tool-desc">{r.detail}</span>}
                        </span>
                      </label>
                    ))}
                    {visible.length === 0 && (
                      <p className="tools-hint">Nothing matches that filter.</p>
                    )}
                  </div>

                  {picked.size === 0 && (
                    <p className="tools-hint">
                      Select at least one — a connection allowed nothing would just fail
                      every call. To switch the account off, use the switch on its card.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <footer className="modal-foot">
          <button className="btn btn-subtle" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn"
            onClick={() => void save()}
            disabled={!savable || saving}
          >
            {saving
              ? "Saving…"
              : all
                ? `Allow all ${noun}`
                : `Allow ${picked.size} of ${resources.length}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
